import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Menu,
    ListItemIcon,
    ListItemText,
    Button,
    CircularProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { extractList, extractPagination } from '../utils/response';
import { useDebounce } from '../hooks/useDebounce';
import { CREATE_TEAM } from '../navigations/path';
import { getTeams, deleteTeam } from '../store/slices/teamsSlice';
import SweetAlert from '../utils/sweetAlert';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../hooks/useAuth';
import Can from './permissions/Can';

const transformTeamsData = (apiTeams) => {
    if (!Array.isArray(apiTeams)) return [];
    return apiTeams.map((team) => ({
        id: team.id,
        name: team.name,
        lead: team.members?.find((member) => member.role === 'lead')?.name || 'No Lead',
        leadId: team.members?.find((member) => member.role === 'lead')?.id,
        membersCount: team.members?.length ?? 0,
        members: team.members ?? [],
        created: team.created_at,
        status: team.status?.name || 'active',
    }));
};

const TeamTableList = () => {
    const dispatch = useDispatch();
    const { hasPermission } = useAuth();

    const canViewListTeam = hasPermission('team-list');
    const canCreateTeam = hasPermission('team-create');
    const canEditTeam = hasPermission('team-edit');
    const canViewTeam = hasPermission('team-view');
    const canDeleteTeam = hasPermission('team-delete');

    const navigate = useNavigate();
    const [teams, setTeams] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 400);
    const [filterStatus, setFilterStatus] = useState('all');
    const [error, setError] = useState(null);
    const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
    const [actionMenuTeam, setActionMenuTeam] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const availableStatuses = ['all', 'active', 'inactive'];
    const permissionDeniedToastShown = useRef(false);

    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, filterStatus]);

    const fetchTeams = useCallback(async () => {
        if (!canViewListTeam) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const params = {
                paginate: 1,
                page: page + 1,
                per_page: rowsPerPage,
            };
            const q = debouncedSearch.trim();
            if (q) params.search = q;
            if (filterStatus !== 'all') {
                params.status = filterStatus;
            }

            const response = await dispatch(getTeams(params)).unwrap();
            const list = extractList(response);
            const pag = extractPagination(response);
            setTeams(transformTeamsData(list));
            setTotal(Number(pag.total) || 0);
        } catch (err) {
            setError('Failed to load teams. Please try again.');
            setTeams([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [canViewListTeam, dispatch, page, rowsPerPage, debouncedSearch, filterStatus]);

    useEffect(() => {
        if (!canViewListTeam) {
            setLoading(false);
            if (!permissionDeniedToastShown.current) {
                permissionDeniedToastShown.current = true;
                toast.error('You do not have permission to view teams.');
            }
            return;
        }
        permissionDeniedToastShown.current = false;
        fetchTeams();
    }, [canViewListTeam, fetchTeams]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleEdit = (teamId) => {
        if (!canEditTeam) return;
        navigate(`${CREATE_TEAM}?mode=edit&id=${teamId}`);
    };

    const handleView = (teamId) => {
        if (!canViewTeam) return;
        navigate(`${CREATE_TEAM}?mode=view&id=${teamId}`);
    };

    const handleDelete = async (teamId) => {
        if (!canDeleteTeam) return;
        try {
            const result = await SweetAlert.deleteConfirm('this Team');
            if (!result.isConfirmed) return;

            await dispatch(deleteTeam(teamId)).unwrap();
            await SweetAlert.success({
                text: 'team deleted successfully.',
            });
            await fetchTeams();
        } catch (err) {
            console.error('Delete action error:', err);
            await SweetAlert.error({
                text: 'Failed to delete team record',
            });
        }
    };

    const handleCreateTeam = () => {
        if (!canCreateTeam) return;
        navigate(CREATE_TEAM);
    };

    const openActionMenu = (event, team) => {
        event.stopPropagation();
        setActionMenuAnchor(event.currentTarget);
        setActionMenuTeam(team);
    };

    const closeActionMenu = () => {
        setActionMenuAnchor(null);
        setActionMenuTeam(null);
    };

    const handleMenuView = () => {
        if (actionMenuTeam) handleView(actionMenuTeam.id);
        closeActionMenu();
    };

    const handleMenuEdit = () => {
        if (actionMenuTeam) handleEdit(actionMenuTeam.id);
        closeActionMenu();
    };

    const handleMenuDelete = () => {
        if (actionMenuTeam) handleDelete(actionMenuTeam.id);
        closeActionMenu();
    };

    const getStatusChip = (status) => {
        const color = status === 'active' ? 'success' : 'error';
        const label = status === 'active' ? 'Active' : 'Inactive';
        return (
            <Chip
                label={label}
                color={color}
                size="small"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
            />
        );
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const hasActiveFilters = Boolean(debouncedSearch.trim()) || filterStatus !== 'all';
    const showFirstTeamEmpty =
        canViewListTeam && !loading && total === 0 && !hasActiveFilters && !error;
    const showNoMatch = canViewListTeam && !loading && total === 0 && hasActiveFilters && !error;

    return (
            <Box sx={{ flexGrow: 1, p: 4, bgcolor: 'background.paper' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                        Teams List ({total})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Can permission="team-create">
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleCreateTeam}
                                disabled={loading}
                                sx={{ borderRadius: 2, px: { xs: 2, sm: 3 }, py: { xs: 1, sm: 1.5 } }}
                            >
                                Add Team
                            </Button>
                        </Can>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        gap: 3,
                        mb: 3,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 1,
                    }}
                >
                    <TextField
                        label="Search Name or Lead"
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ minWidth: 250 }}
                        disabled={loading}
                    />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel id="status-filter-label">Filter by Status</InputLabel>
                        <Select
                            labelId="status-filter-label"
                            value={filterStatus}
                            label="Filter by Status"
                            onChange={(e) => setFilterStatus(e.target.value)}
                            disabled={loading}
                            sx={{ borderRadius: 2 }}
                        >
                            {availableStatuses.map((status) => (
                                <MenuItem key={status} value={status}>
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography variant="body2" color="textSecondary" sx={{ ml: 'auto' }}>
                        {total > 0 && teams.length > 0
                            ? `Showing ${page * rowsPerPage + 1}–${page * rowsPerPage + teams.length} of ${total}`
                            : null}
                    </Typography>
                </Box>

                {error && (
                    <Paper elevation={3} sx={{ p: 3, mb: 3, textAlign: 'center' }}>
                        <Typography variant="body1" color="error" gutterBottom>
                            {error}
                        </Typography>
                        <Button variant="contained" color="primary" onClick={() => fetchTeams()}>
                            Retry
                        </Button>
                    </Paper>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
                        <CircularProgress color="primary" />
                    </Box>
                )}

                {showFirstTeamEmpty && (
                    <Paper elevation={3} sx={{ p: 4, textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                            No teams found
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            Get started by creating your first team.
                        </Typography>
                        <Can permission="team-create">
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleCreateTeam}
                                size="large"
                            >
                                Create Your First Team
                            </Button>
                        </Can>
                    </Paper>
                )}

                {showNoMatch && (
                    <Box sx={{ mt: 2, textAlign: 'center', p: 4 }}>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                            No teams match your search
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            Try adjusting your search terms or filters.
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                                setSearchTerm('');
                                setFilterStatus('all');
                                setPage(0);
                            }}
                        >
                            Clear Filters
                        </Button>
                    </Box>
                )}

                {canViewListTeam && !loading && !error && total > 0 && (
                    <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <TableContainer sx={{ maxHeight: 600 }}>
                            <Table stickyHeader aria-label="team list table">
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            bgcolor: 'primary.main',
                                            '& th': { color: 'primary.contrastText', fontWeight: 'bold', border: 0 },
                                        }}
                                    >
                                        <TableCell>Team Name</TableCell>
                                        <TableCell>Team Lead</TableCell>
                                        <TableCell>Members</TableCell>
                                        <TableCell>Created</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {teams.map((team) => (
                                        <TableRow
                                            key={team.id}
                                            hover
                                            sx={{
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                transition: 'background-color 0.2s ease',
                                            }}
                                        >
                                            <TableCell sx={{ fontWeight: 'medium' }}>{team.name}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ color: 'primary.main', fontWeight: 'medium' }}
                                                    >
                                                        {team.lead}
                                                    </Typography>
                                                    {team.leadId && (
                                                        <Chip
                                                            label="Lead"
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                            sx={{ fontSize: '0.7rem' }}
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={`${team.membersCount} member${team.membersCount !== 1 ? 's' : ''}`}
                                                    size="small"
                                                    color="default"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.7rem' }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="textSecondary">
                                                    {formatDate(team.created)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{getStatusChip(team.status)}</TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    aria-label="Actions"
                                                    onClick={(e) => openActionMenu(e, team)}
                                                    sx={{ color: 'text.secondary' }}
                                                >
                                                    <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Menu
                            anchorEl={actionMenuAnchor}
                            open={Boolean(actionMenuAnchor)}
                            onClose={closeActionMenu}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            PaperProps={{ sx: { minWidth: 120 } }}
                        >
                            <Can permission="team-view">
                                <MenuItem onClick={handleMenuView}>
                                    <ListItemIcon>
                                        <VisibilityIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>View</ListItemText>
                                </MenuItem>
                            </Can>
                            <Can permission="team-edit">
                                <MenuItem onClick={handleMenuEdit}>
                                    <ListItemIcon>
                                        <EditIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>Edit</ListItemText>
                                </MenuItem>
                            </Can>
                            <Can permission="team-delete">
                                <MenuItem onClick={handleMenuDelete} sx={{ color: 'error.main' }}>
                                    <ListItemIcon>
                                        <DeleteIcon fontSize="small" color="error" />
                                    </ListItemIcon>
                                    <ListItemText>Delete</ListItemText>
                                </MenuItem>
                            </Can>
                        </Menu>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={total}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Rows per page"
                        />
                    </Paper>
                )}
            </Box>
    );
};

export default TeamTableList;
